import React, { useState } from "react"

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Typography,
    TextField,
    Box,
    Tooltip,
    Autocomplete,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
} from "@mui/material"
import LoadingButton from "@mui/lab/LoadingButton"

import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"

import { GroupChip } from "../../Groups/GroupBadge"
import AddEnterpriseGroupForm from "../../Crm/TabTables/EnterpriseTable/AddEnterpriseGroupForm"

import api from "../../../api"
import { getSession } from "../../../auth"
import useNotification from "../../Notification"

let timeoutId

const BulkEditEnterpriseDialog = ({ open, handleClose, handleSubmit, customFields = [] }) => {
    const session = getSession()
    const isAdminOrSupervisor = (session.groups || []).some(each => each.name === "administrador" || each.name === "supervisor")
    const canCreateSegments = isAdminOrSupervisor || session.profile.enterprise.settings.agent_can_create_segments

    const [customFieldsData, setCustomFieldsData] = useState({})
    const [selectedGroups, setSelectedGroups] = useState([])
    const [contactGroups, setContactGroups] = useState([])
    const [groupsLoading, setGroupsLoading] = useState(false)
    const [groupsSelectOpen, setGroupsSelectOpen] = useState(false)
    const [nextGroupsPointer, setNextGroupsPointer] = useState(null)
    const [loading, setLoading] = useState(false)
    const [showNewSegmentModal, setShowNewSegmentModal] = useState(false)

    const getGroups = async (search, groupsUrl) => {
        if (groupsUrl === undefined || (typeof search !== 'object' && search !== undefined)) {
            setContactGroups([])
        }

        const enterpriseId = getSession().profile.enterprise.id

        let url
        if (groupsUrl !== undefined)
            url = `/api/${groupsUrl.split('/api/')[1]}`
        else {
            url = `/api/enterprises/${enterpriseId}/contact_group/noserialize/`
            if (typeof search !== 'object' && search !== undefined)
                url = `${url}?search=${search}`
        }

        setGroupsSelectOpen(true)
        setGroupsLoading(true)
        const response = await api.get(url)
        setGroupsLoading(false)

        if (response.status === 200) {
            setNextGroupsPointer(response.data.next)
            setContactGroups((prev) => [
                ...prev,
                ...response.data.results.filter(newItem => !prev.some(existing => existing.id === newItem.id))
            ])
        }
    }

    const handleScroll = (event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.target
        if (nextGroupsPointer && scrollHeight - scrollTop <= clientHeight + 5)
            getGroups(undefined, nextGroupsPointer)
    }

    const handleUpdateGroups = (updater) => {
        const updatedGroups = updater(contactGroups)
        const newGroup = updatedGroups.length > contactGroups.length
            ? updatedGroups.find(group => !contactGroups.some(p => p.id === group.id))
            : null
        setContactGroups(updatedGroups)
        if (newGroup)
            setSelectedGroups(prev => prev.some(g => g.id === newGroup.id) ? prev : [...prev, newGroup])
    }

    const subsObj = (list, item) => {
        if (list.some(existing => existing.id === item.id))
            return list
        return [item, ...list]
    }

    const submit = async () => {
        setLoading(true)
        await handleSubmit({ customFieldsData, selectedGroups })
        setLoading(false)
    }

    return (
        <Dialog
            open={open}
            keepMounted
            onClose={() => {}}
            aria-labelledby="bulk-edit-dialog-title"
            fullWidth
            maxWidth="sm"
            sx={{ position: "absolute" }}
        >
            <DialogTitle id="bulk-edit-dialog-title" className="group-flexbox">
                Editar empresas selecionadas
                <Tooltip title="Fechar">
                    <Button className="action-button">
                        <CloseIcon onClick={handleClose} />
                    </Button>
                </Tooltip>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <DialogContentText id="bulk-edit-dialog-description">
                    {customFields && customFields.length > 0 && (
                        <>
                            <Typography marginBottom="1rem" fontSize="19px" fontWeight="500" color="#21446c">
                                Campos Customizados
                            </Typography>
                            {customFields.map((field) => {
                                const fieldValue = Object.prototype.hasOwnProperty.call(customFieldsData, field.id)
                                    ? customFieldsData[field.id]
                                    : ''

                                if (field.field_type === 'boolean') {
                                    return (
                                        <FormControl key={field.id} fullWidth size="small" className="push-bottom">
                                            <InputLabel>{field.field_name}</InputLabel>
                                            <Select
                                                value={fieldValue}
                                                label={field.field_name}
                                                onChange={(e) => setCustomFieldsData((prev) => {
                                                    const next = { ...prev }
                                                    if (e.target.value === '')
                                                        delete next[field.id]
                                                    else
                                                        next[field.id] = e.target.value
                                                    return next
                                                })}
                                            >
                                                <MenuItem value=""><em>Sem alteração</em></MenuItem>
                                                <MenuItem value="true">Sim</MenuItem>
                                                <MenuItem value="false">Não</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )
                                } else if (field.field_type === 'select') {
                                    return (
                                        <FormControl key={field.id} fullWidth size="small" className="push-bottom">
                                            <InputLabel>{field.field_name}</InputLabel>
                                            <Select
                                                value={fieldValue}
                                                label={field.field_name}
                                                onChange={(e) => setCustomFieldsData((prev) => ({
                                                    ...prev,
                                                    [field.id]: e.target.value
                                                }))}
                                            >
                                                <MenuItem value="">
                                                    <em>Nenhum</em>
                                                </MenuItem>
                                                {field.options?.map((option) => (
                                                    <MenuItem key={option.id} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )
                                } else if (field.field_type === 'textarea') {
                                    return (
                                        <TextField
                                            key={field.id}
                                            size="small"
                                            label={field.field_name}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            variant="outlined"
                                            value={fieldValue}
                                            onChange={(e) => setCustomFieldsData((prev) => ({
                                                ...prev,
                                                [field.id]: e.target.value
                                            }))}
                                            className="push-bottom"
                                        />
                                    )
                                } else if (field.field_type === 'date') {
                                    return (
                                        <TextField
                                            key={field.id}
                                            size="small"
                                            label={field.field_name}
                                            fullWidth
                                            type="date"
                                            variant="outlined"
                                            value={fieldValue}
                                            onChange={(e) => setCustomFieldsData((prev) => ({
                                                ...prev,
                                                [field.id]: e.target.value
                                            }))}
                                            className="push-bottom"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )
                                } else {
                                    return (
                                        <TextField
                                            key={field.id}
                                            size="small"
                                            label={field.field_name}
                                            fullWidth
                                            type={field.field_type === 'integer' ? 'number' : 'text'}
                                            variant="outlined"
                                            value={fieldValue}
                                            onChange={(e) => setCustomFieldsData((prev) => ({
                                                ...prev,
                                                [field.id]: e.target.value
                                            }))}
                                            className="push-bottom"
                                        />
                                    )
                                }
                            })}
                        </>
                    )}

                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography marginBottom="1rem" fontSize="19px" fontWeight="500" color="#21446c">
                            Segmentos
                        </Typography>
                        {canCreateSegments && (
                            <IconButton
                                size="small"
                                onClick={() => setShowNewSegmentModal(true)}
                                aria-label="Criar segmento"
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    <Autocomplete
                        multiple
                        sx={{ marginTop: "1rem", marginBottom: "1rem" }}
                        size="small"
                        open={groupsSelectOpen}
                        onOpen={getGroups}
                        onClose={() => setGroupsSelectOpen(false)}
                        isOptionEqualToValue={(option, value) => option.name === value.name}
                        getOptionLabel={(option) => option.name || ""}
                        options={contactGroups}
                        loading={groupsLoading}
                        filterOptions={(x) => x}
                        onInputChange={(event) => {
                            if (timeoutId)
                                clearTimeout(timeoutId)
                            timeoutId = setTimeout(() => getGroups(event.target.value), 1000)
                        }}
                        value={selectedGroups}
                        onChange={(event, newValue) => setSelectedGroups(newValue)}
                        ListboxProps={{ onScroll: handleScroll }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <GroupChip
                                    {...getTagProps({ index })}
                                    label={option.name}
                                    isEnterprise={true}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Segmentos"
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {groupsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                    {showNewSegmentModal && (
                        <AddEnterpriseGroupForm
                            open={showNewSegmentModal}
                            handleClose={() => setShowNewSegmentModal(false)}
                            submit={() => Promise.resolve()}
                            enterpriseId={session.profile.enterprise.id}
                            selectionIds={[]}
                            updateReady={() => {}}
                            updateGroups={handleUpdateGroups}
                            updateEnterprises={() => {}}
                            subsObj={subsObj}
                            updateForceReload={() => {}}
                            optionalTitle="Criar novo segmento de empresas"
                        />
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary" disabled={loading}>
                    Cancelar
                </Button>
                <LoadingButton onClick={submit} color="primary" loading={loading}>
                    Enviar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default BulkEditEnterpriseDialog
